/* Ingredient drawers: native details without JS, overlay drawers after enhancement. */
(() => {
  if (window.AuluoFormulaV8) return;
  const controllers = new WeakMap();
  const active = new Set();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motion = {duration: 380, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'both'};

  function init(scope = document) {
    scope.querySelectorAll('details.auluo-formula-cell').forEach(cell => {
      if (controllers.has(cell)) return;
      const summary = cell.querySelector(':scope > summary');
      const body = cell.querySelector(':scope > .auluo-formula-cell-body');
      const inner = body?.firstElementChild;
      if (!summary || !body || !inner) return;
      const grid = cell.closest('[data-auluo-formula-grid]');
      let desired = cell.open;
      let physical = cell.open;
      let animation = null;
      cell.dataset.formulaEnhanced = '';
      cell.removeAttribute('name');
      summary.setAttribute('aria-expanded', String(desired));

      function finish() {
        if (animation) {
          animation.onfinish = null;
          animation.cancel();
          animation = null;
        }
        cell.open = desired;
        physical = desired;
        body.style.removeProperty('height');
        cell.removeAttribute('data-formula-state');
        if (!desired) active.delete(controller);
      }

      function setOpen(value, animate = true) {
        if (value === desired) {
          if (!animate && animation) { finish(); return; }
          if (animation || cell.open === value) return;
        }
        if (value) {
          grid?.querySelectorAll('details.auluo-formula-cell').forEach(other => {
            if (other !== cell) controllers.get(other)?.setOpen(false, false);
          });
        }
        const startHeight = cell.open ? body.getBoundingClientRect().height : 0;
        const startOpacity = cell.open ? getComputedStyle(body).opacity : '0';
        const startTransform = cell.open ? getComputedStyle(body).transform : 'translateY(-8px)';
        if (animation) { animation.onfinish = null; animation.cancel(); animation = null; }
        desired = value;
        summary.setAttribute('aria-expanded', String(value));
        if (value) active.add(controller);
        if (!animate || reduced.matches || typeof body.animate !== 'function') { finish(); return; }
        cell.open = true;
        physical = true;
        cell.dataset.formulaState = value ? 'opening' : 'closing';
        const style = getComputedStyle(body);
        const edge = (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
        const targetHeight = value ? inner.getBoundingClientRect().height + edge : 0;
        try {
          animation = body.animate([
            {height: `${startHeight}px`, opacity: startOpacity, transform: startTransform},
            {height: `${targetHeight}px`, opacity: value ? 1 : 0, transform: value ? 'translateY(0)' : 'translateY(-8px)'}
          ], motion);
          animation.onfinish = finish;
        } catch (_) { finish(); }
      }

      const controller = {cell, summary, setOpen, finish};
      controllers.set(cell, controller);
      summary.addEventListener('click', event => {
        if (event.defaultPrevented || event.target.closest('a, button, input, select, textarea')) return;
        event.preventDefault();
        setOpen(!desired);
      });
      cell.addEventListener('toggle', () => {
        if (cell.open === physical) return;
        const value = cell.open;
        desired = !value;
        physical = value;
        setOpen(value, false);
      });
      cell.addEventListener('keydown', event => {
        if (event.key === 'Escape' && desired) {
          event.preventDefault();
          setOpen(false);
          summary.focus({preventScroll: true});
        }
      });
      cell.addEventListener('focusout', event => {
        // Safari can clear focus after a finger tap. Only a real focus move
        // outside the card closes it; the outside-click handler covers touch.
        if (!event.relatedTarget) return;
        requestAnimationFrame(() => {
          if (!cell.contains(document.activeElement)) setOpen(false);
        });
      });
      if (desired) active.add(controller);
    });
  }

  document.addEventListener('click', event => {
    active.forEach(controller => {
      if (!controller.cell.contains(event.target)) controller.setOpen(false);
    });
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    active.forEach(controller => {
      controller.setOpen(false);
      controller.summary.focus({preventScroll: true});
    });
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    active.forEach(controller => {
      if (event.target.contains(controller.cell)) controller.setOpen(false, false);
    });
  });
  document.addEventListener('shopify:block:select', event => {
    const cell = event.target.closest('details.auluo-formula-cell');
    if (cell) controllers.get(cell)?.setOpen(true, false);
  });
  let lastWidth = document.documentElement.clientWidth;
  window.addEventListener('resize', () => {
    const width = document.documentElement.clientWidth;
    if (width === lastWidth) return; // Mobile browser bars change height while scrolling.
    lastWidth = width;
    active.forEach(controller => controller.finish());
  }, {passive: true});
  reduced.addEventListener('change', () => active.forEach(controller => controller.finish()));
  window.AuluoFormulaV8 = {init};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
})();
